// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import type { AutodeskCloudModel } from '@evolvemep/api-ts';
import { Box, Flex, Stack, Title } from '@mantine/core';

import { useAutodeskExtensionStoreSnapshot } from 'components/Autodesk/useAutodeskExtensionStore';
import { DraggableDivider } from 'components/Mantine/DraggableDivider';
import { isNotNil } from 'helpers/isNotNil';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useMutableSize } from 'hooks/useMutableSize';
import { useSpoolRequestsViewerState } from 'modules/Shop/Spooling/hooks/useSpoolRequestsViewerState';
import { SpoolIsolationPopover } from 'modules/Shop/Spooling/SpoolIsolationPopover';

import { SpoolAssignmentPane } from './Assigning/SpoolAssignmentPane';
import { SpoolForgeViewerPane } from './SpoolForgeViewerPane';
import { RequestingNewSpoolsHeaderBar } from './SpoolRequests/RequestingNewSpoolsHeaderBar';

const MIN_PANEL_WIDTH = 350;
const DEFAULT_PANEL_WIDTH = 400;

export const SpoolingBrowser = ({
  selectedUrn,
  model,
}: {
  selectedUrn: string | null;
  model: AutodeskCloudModel | null;
}) => {
  const { hasSpoolingAccess } = useSpoolRequestsViewerState();
  const [requestingNewSpools, setRequestingNewSpools] = useState(false);
  const [panel, setPanelWidth] = useMutableSize<HTMLDivElement>({
    defaultSize: DEFAULT_PANEL_WIDTH,
    min: requestingNewSpools ? 0 : MIN_PANEL_WIDTH,
    max: 600,
  });

  // Collapse the panel when requesting a new spool, and expand it when not
  useEffect(() => {
    setPanelWidth(requestingNewSpools ? 0 : DEFAULT_PANEL_WIDTH);
  }, [requestingNewSpools, setPanelWidth]);

  const [spoolingSettings, setSpoolingSettings] = useLocalStorage('SPOOLING_SETTINGS', {
    fitToView: true,
    ghosting: false,
    colorKeyEnabled: true,
  });

  const { commands } = useAutodeskExtensionStoreSnapshot('apsViewer');
  const { ghosting } = spoolingSettings;
  useEffect(() => {
    commands?.setGhosting(ghosting);
  }, [commands, ghosting]);

  return (
    <>
      <Box h="100%" ref={panel} style={{ overflow: 'hidden' }}>
        <Stack miw={MIN_PANEL_WIDTH} h="100%" gap={0}>
          <SpoolAssignmentPane
            spoolingSettings={spoolingSettings}
            requestingNewSpools={requestingNewSpools}
            model={model}
          />
        </Stack>
      </Box>

      <DraggableDivider onDragDivider={(delta) => setPanelWidth((h) => h + delta)} orientation="vertical" />

      <SpoolForgeViewerPane
        selectedUrn={selectedUrn}
        headerSection={
          <>
            <Flex gap="xs" align="center">
              <Title order={4}>Model browser</Title>
              <SpoolIsolationPopover
                spoolingSettings={spoolingSettings}
                toggleSetting={(setting) => setSpoolingSettings((s) => ({ ...s, [setting]: !s[setting] }))}
              />
            </Flex>
            {hasSpoolingAccess && isNotNil(model) && (
              <RequestingNewSpoolsHeaderBar
                urn={model.urn}
                spoolingSettings={spoolingSettings}
                requestingNewSpools={requestingNewSpools}
                setRequestingNewSpools={setRequestingNewSpools}
              />
            )}
          </>
        }
      />
    </>
  );
};
